import { AddRounded, UndoRounded, VisibilityOutlined } from "@mui/icons-material";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { fetchFinanceRefunds } from "@/api/invoices";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter } from "@/components/BlotterFilterBar";
import BlotterNote from "@/components/BlotterNote";
import NewRefundDialog from "@/components/finance/NewRefundDialog";
import RefundDetailDialog from "@/components/finance/RefundDetailDialog";
import ReverseRefundDialog from "@/components/finance/ReverseRefundDialog";
import { useFinanceRefunds } from "@/hooks/useInvoices";
import { usePermissions } from "@/hooks/usePermissions";
import type { Refund } from "@/interfaces/invoices";

const shortDate = (iso: string | null) => (iso ? iso.slice(0, 10) : "—");
const dateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";
const money = (v: string | null, ccy = "usd") =>
  v == null
    ? "—"
    : `${ccy.toUpperCase()} ${Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const orgName = (r: Refund) => r.organizationName ?? "—";
const groupName = (r: Refund) => r.groupName ?? "—";
const invoiceRef = (r: Refund) => r.invoiceNumber ?? `#${r.invoiceId}`;
const actorName = (r: Refund) => r.actorName ?? "—";

/**
 * The GFG Finance cross-org REFUNDS blotter — recorded client refunds (out-of-band money-back, kept
 * for bookkeeping). GFG-only (view/FINANCE). Recording a refund (New Refund) is gated cancel/FINANCE
 * and never changes invoice status or credits. Fully server-side (paginated + searched).
 */
export default function FinanceRefundsPanel() {
  const { can } = usePermissions();
  const canManage = can("cancel", "FINANCE");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reverseTarget, setReverseTarget] = useState<Refund | null>(null);
  const [detail, setDetail] = useState<Refund | null>(null);
  // Reversible = an original refund (not itself a reversal) that hasn't been reversed yet.
  const isReversible = (r: Refund) => r.reversesRefundId == null && !r.isReversed;

  const useData = (q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<Refund> => {
    const { data, isLoading } = useFinanceRefunds({
      search: q.search || undefined,
      sortBy: q.sortBy,
      sortDir: q.sortDir,
      page: q.page,
      limit: q.limit,
    });
    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };

  const config: BlotterConfig<Refund> = {
    title: "Refunds",
    searchPlaceholder: "Search reference, reason, or invoice #",
    itemNoun: "refund",
    rowKey: (r) => r.id,
    defaultSortKey: "refundDate",
    defaultSortDir: "desc",
    filterFields: [],
    useData,
    exportFilename: "finance-refunds.csv",
    // Row actions: View (eye) is always available; Reverse only for GFG on an original, unreversed refund.
    leadingCell: (r) => (
      <Box sx={{ display: "flex", gap: 0.25, whiteSpace: "nowrap" }}>
        <Tooltip title="View details" arrow>
          <IconButton size="small" aria-label="View refund details" onClick={() => setDetail(r)}>
            <VisibilityOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        {canManage && isReversible(r) && (
          <Tooltip title="Reverse this refund" arrow>
            <IconButton
              size="small"
              aria-label="Reverse refund"
              onClick={() => setReverseTarget(r)}
            >
              <UndoRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    headerAction: canManage ? (
      <IconButton
        onClick={() => setDialogOpen(true)}
        title="New refund"
        aria-label="New refund"
        sx={{
          bgcolor: "var(--color-rodeo-black)",
          color: "var(--color-white)",
          borderRadius: "var(--radius-btn)",
          width: 48,
          height: 48,
          "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
        }}
      >
        <AddRounded />
      </IconButton>
    ) : undefined,
    exportFetch: async (q) => {
      const res = await fetchFinanceRefunds({
        search: q.search || undefined,
        page: q.page,
        limit: q.limit,
      });
      return { data: res.data, totalPages: res.totalPages };
    },
    columns: [
      {
        key: "refundDate",
        label: "Refund date",
        sortable: true,
        sortValue: (r) => r.refundDate,
        render: (r) => shortDate(r.refundDate),
      },
      {
        key: "invoice",
        label: "Invoice",
        sortValue: invoiceRef,
        render: (r) => <Box sx={{ fontWeight: 500 }}>{invoiceRef(r)}</Box>,
      },
      {
        key: "type",
        label: "Type",
        sortValue: (r) => (r.reversesRefundId != null ? 1 : 0),
        render: (r) =>
          r.reversesRefundId != null ? (
            <Chip size="small" label="Reversal" color="default" variant="outlined" />
          ) : (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
              <Chip size="small" label="Refund" color="primary" variant="outlined" />
              {r.isReversed && (
                <Typography sx={{ fontSize: 11, color: "var(--color-graphite)", opacity: 0.8 }}>
                  reversed
                </Typography>
              )}
            </Box>
          ),
        exportValue: (r) =>
          r.reversesRefundId != null ? "Reversal" : r.isReversed ? "Refund (reversed)" : "Refund",
      },
      { key: "organization", label: "Organization", sortValue: orgName, render: orgName },
      { key: "group", label: "Group", sortValue: groupName, render: groupName },
      {
        key: "amount",
        label: "Amount",
        sortable: true,
        sortValue: (r) => Number(r.amount ?? 0),
        render: (r) => money(r.amount, r.currency),
        exportValue: (r) => money(r.amount, r.currency),
      },
      {
        key: "reference",
        label: "Reference",
        render: (r) => r.reference ?? "—",
        exportValue: (r) => r.reference ?? "",
      },
      { key: "actor", label: "Recorded by", sortValue: actorName, render: actorName },
      {
        key: "created",
        label: "Recorded",
        sortable: true,
        sortValue: (r) => r.createdAt,
        render: (r) => dateTime(r.createdAt),
      },
    ],
  };

  return (
    <Box>
      <BlotterNote>
        <strong>Refunds recorded against invoice payments.</strong> When a payment on a GFG invoice
        is refunded to the client <strong>out of band</strong> (in Stripe or the bank), log it here
        for the books. This is a bookkeeping record only — it does <strong>not</strong> change the
        invoice status or the client's credit balance; those are reconciled separately. Use{" "}
        <strong>New Refund</strong> to record one against a specific invoice payment.
      </BlotterNote>
      <Blotter config={config} />
      <NewRefundDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <ReverseRefundDialog refund={reverseTarget} onClose={() => setReverseTarget(null)} />
      <RefundDetailDialog refund={detail} onClose={() => setDetail(null)} />
    </Box>
  );
}
